import api from './axiosConfig';

class AuthService {
  // User Registration
  async register(userData) {
    try {
      const response = await api.post('/users/register/', userData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // User Login
  async login(credentials) {
    try {
      const response = await api.post('/users/token/', credentials);
      const { access, refresh } = response.data;
      
      // Store tokens
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Refresh Token
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post('/users/token/refresh/', {
        refresh: refreshToken
      });
      
      const { access } = response.data;
      localStorage.setItem('access_token', access);
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Password Reset Request
  async requestPasswordReset(email) {
    try {
      const response = await api.post('/users/password_reset/', { email });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Password Reset Confirm
  async confirmPasswordReset(uid, token, newPassword) {
    try {
      const response = await api.post('/users/password_reset/confirm/', {
        uid,
        token,
        new_password1: newPassword,
        new_password2: newPassword
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get Current User
  async getCurrentUser() {
    try {
      const response = await api.get('/users/me/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get User Profile
  async getUserProfile() {
    try {
      const response = await api.get('/users/me/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update User Profile
  async updateUserProfile(profileData) {
    try {
      const config = {};
      
      // If profileData is FormData (for file uploads), don't set Content-Type
      // Let the browser set it with the boundary
      if (!(profileData instanceof FormData)) {
        config.headers = {
          'Content-Type': 'application/json'
        };
      }
      
      const response = await api.patch('/users/me/profile/', profileData, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get User Preferences
  async getUserPreferences() {
    try {
      const response = await api.get('/users/me/preferences/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update User Preferences
  async updateUserPreferences(preferencesData) {
    try {
      const response = await api.patch('/users/me/preferences/', preferencesData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get User Privacy Settings
  async getUserPrivacy() {
    try {
      const response = await api.get('/users/me/privacy/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update User Privacy Settings
  async updateUserPrivacy(privacyData) {
    try {
      const response = await api.patch('/users/me/privacy/', privacyData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get User Security Settings
  async getUserSecurity() {
    try {
      const response = await api.get('/users/me/security/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update User Security Settings
  async updateUserSecurity(securityData) {
    try {
      const response = await api.patch('/users/me/security/', securityData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Set or change account password (Google-only users can set without current password)
  async setAccountPassword({ currentPassword, newPassword, newPasswordConfirm }) {
    try {
      const payload = {
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      };
      if (currentPassword) {
        payload.current_password = currentPassword;
      }
      const response = await api.post('/users/me/security/password/', payload);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get User Actions/Activity Log
  async getUserActions() {
    try {
      const response = await api.get('/users/me/actions/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get User Sessions
  async getUserSessions() {
    try {
      const response = await api.get('/users/me/sessions/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Terminate User Session
  async terminateSession(sessionId) {
    try {
      const response = await api.delete(`/users/me/sessions/${sessionId}/terminate/`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Terminate All Other Sessions
  async terminateAllSessions() {
    try {
      const response = await api.delete('/users/me/sessions/terminate-all/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Search Users (for admin/moderator)
  async searchUsers(query) {
    try {
      const response = await api.get(`/users/users/search/?q=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Admin: Get All Users
  async getAllUsers() {
    try {
      const response = await api.get('/users/admin/users/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Admin: Get User by ID
  async getUserById(userId) {
    try {
      const response = await api.get(`/users/admin/users/${userId}/`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Admin: Update User
  async updateUser(userId, userData) {
    try {
      const response = await api.patch(`/users/admin/users/${userId}/`, userData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Admin: Change User Role
  async changeUserRole(userId, newRole, reason = '') {
    try {
      const response = await api.post(`/users/admin/users/${userId}/change-role/`, {
        new_role: newRole,
        reason: reason
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Admin: Lock User Account
  async lockUserAccount(userId, durationMinutes = 30) {
    try {
      const response = await api.post(`/users/admin/users/${userId}/lock/`, {
        duration_minutes: durationMinutes
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Admin: Unlock User Account
  async unlockUserAccount(userId) {
    try {
      const response = await api.post(`/users/admin/users/${userId}/unlock/`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Admin: Get User Role History
  async getUserRoleHistory(userId) {
    try {
      const response = await api.get(`/users/admin/users/${userId}/role-history/`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Email Verification
  async verifyEmail(token) {
    try {
      const response = await api.get(`/users/verify-email/${token}/`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Google OAuth Login
  async googleLogin(accessToken) {
    try {
      const response = await api.post('/users/google/', {
        access_token: accessToken
      });
      
      const { access, refresh } = response.data;
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Logout: clear tokens and user preferences so they are not visible when logged out
  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    try {
      localStorage.removeItem('theme-preference');
    } catch (_) {}
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app-logout'));
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  }

  // Get stored access token
  getAccessToken() {
    return localStorage.getItem('access_token');
  }

  // Handle API errors (error may be raw axios error or AppError from axios interceptor)
  handleError(error) {
    const response = error.response ?? (error.details?.status != null ? { data: error.details?.data, status: error.details?.status } : null);
    if (response) {
      const { data, status } = response;
      
      if (status === 400) {
        const oauthCode = data?.errors?.code;
        if (oauthCode === 'oauth_account') {
          return {
            type: 'oauth_account',
            message: data?.message || 'This email is signed in with Google. Use Google or set a password in account settings.',
            errors: data?.errors || { code: 'oauth_account', provider: 'google' },
          };
        }
        // Use formatted message from AppError (interceptor) when present, else build from data
        let errorMessage = (error.message && error.message !== 'Invalid request') ? error.message : null;
        if (!errorMessage && data?.email && Array.isArray(data.email)) {
          const emailError = data.email[0];
          errorMessage = (emailError.toLowerCase().includes('already exists') || emailError.toLowerCase().includes('user with this email'))
            ? 'This email address is already registered. Please use a different email or try logging in.'
            : emailError;
        } else if (!errorMessage && typeof data?.email === 'string') {
          errorMessage = (data.email.toLowerCase().includes('already exists') || data.email.toLowerCase().includes('user with this email'))
            ? 'This email address is already registered. Please use a different email or try logging in.'
            : data.email;
        } else if (!errorMessage) {
          errorMessage = data?.detail || data?.message || 'Please check your input and try again.';
        }
        return {
          type: 'validation_error',
          message: errorMessage,
          errors: data
        };
      } else if (status === 401) {
        const fromEnvelope =
          data?.success === false && typeof data.message === 'string' ? data.message : null;
        const fromDrf =
          typeof data?.detail === 'string'
            ? data.detail
            : Array.isArray(data?.detail)
              ? data.detail[0]
              : null;
        const fromErrors =
          data?.errors && typeof data.errors === 'object'
            ? (typeof data.errors.detail === 'string' ? data.errors.detail : null)
            : null;
        const msg = fromEnvelope || fromDrf || fromErrors || 'Invalid credentials. Please try again.';
        return {
          type: 'authentication_error',
          message: msg,
          errors: data
        };
      } else if (status === 403) {
        return {
          type: 'permission_error',
          message: 'You do not have permission to perform this action.',
          errors: data
        };
      } else if (status === 404) {
        return {
          type: 'not_found_error',
          message: 'The requested resource was not found.',
          errors: data
        };
      } else if (status >= 500) {
        return {
          type: 'server_error',
          message: 'Server error. Please try again later.',
          errors: data
        };
      } else {
        return {
          type: 'api_error',
          message: data?.detail || 'An error occurred. Please try again.',
          errors: data
        };
      }
    } else if (error.request) {
      // Network error
      return {
        type: 'network_error',
        message: 'Network error. Please check your connection and try again.',
        errors: {}
      };
    } else {
      // Other error
      return {
        type: 'unknown_error',
        message: error.message || 'An unexpected error occurred.',
        errors: {}
      };
    }
  }
}

const authService = new AuthService();

export default authService;
